import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { AuthProvider, useAuth } from "./hooks/useAuth";
import { CompanyProvider } from "./hooks/useCompany";
import { Toaster } from "sonner";

import Layout from "./components/Layout";
import Login from "./pages/Login";
import Dashboard from "./pages/Dashboard";
import Employees from "./pages/hr/Employees";
import ChartOfAccounts from "./pages/finance/ChartOfAccounts";
import JournalEntries from "./pages/finance/JournalEntries";
// Temporarily lazy load new pages to avoid babel stack overflow
import { lazy, Suspense } from "react";
const GeneralLedger = lazy(() => import("./pages/finance/GeneralLedger"));
const TrialBalance = lazy(() => import("./pages/finance/TrialBalance"));
const FinancialStatements = lazy(() => import("./pages/finance/FinancialStatements"));
import Budgets from "./pages/finance/Budgets";
import BudgetPlanning from "./pages/finance/BudgetPlanning";
import BudgetPerformance from "./pages/finance/BudgetPerformance";
import Consolidation from "./pages/Consolidation";
import Settings from "./pages/Settings";
const Reports = lazy(() => import("./pages/Reports"));
const UserManagement = lazy(() => import("./pages/UserManagement"));

// HR Pages
const Payroll = lazy(() => import("./pages/hr/Payroll"));
const PayrollSettings = lazy(() => import("./pages/hr/PayrollSettings"));
const PayrollRunDetail = lazy(() => import("./pages/hr/PayrollRunDetail"));
const Subsidiaries = lazy(() => import("./pages/hr/Subsidiaries"));
const LeaveManagement = lazy(() => import("./pages/hr/LeaveManagement"));
const Recruitment = lazy(() => import("./pages/hr/Recruitment"));

// DFS Pages
import TellerAccounts from "./pages/dfs/TellerAccounts";
import VaultAllocations from "./pages/dfs/VaultAllocations";
import TellerDashboard from "./pages/dfs/TellerDashboard";
import CashPosition from "./pages/dfs/CashPosition";
import StockManagement from "./pages/dfs/StockManagement";
import Customers from "./pages/dfs/Customers";
import Loans from "./pages/dfs/Loans";
import DFSDashboard from "./pages/dfs/DFSDashboard";

import "./App.css";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30000,
      refetchOnWindowFocus: false,
    },
  },
});

function PrivateRoute({ children }) {
  const { isAuthenticated, loading, user } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return children;
}

// Role-based route protection
function RoleRoute({ children, allowedRoles }) {
  const { user } = useAuth();
  
  if (!allowedRoles.includes(user?.role)) {
    // Redirect tellers to their dashboard, others to main dashboard
    if (user?.role === 'teller') {
      return <Navigate to="/dfs/teller-dashboard" replace />;
    }
    return <Navigate to="/dashboard" replace />;
  }
  
  return children;
}

// Teller-specific redirect on root
function RootRedirect() {
  const { user } = useAuth();
  
  if (user?.role === 'teller') {
    return <Navigate to="/dfs/teller-dashboard" replace />;
  }
  return <Dashboard />;
}

function AppRoutes() {
  const LoadingFallback = () => <div className="flex items-center justify-center h-64"><div className="animate-spin h-8 w-8 border-2 border-primary border-t-transparent rounded-full"></div></div>;
  
  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route
        path="/*"
        element={
          <PrivateRoute>
            <Layout>
              <Suspense fallback={<LoadingFallback />}>
                <Routes>
                  <Route path="/" element={<RootRedirect />} />
                  <Route path="/dashboard" element={<RoleRoute allowedRoles={['ceo', 'admin', 'manager', 'accountant', 'viewer']}><Dashboard /></RoleRoute>} />
                  
                  {/* HR Routes */}
                  <Route path="/hr/employees" element={<Employees />} />
                  <Route path="/hr/subsidiaries" element={<Subsidiaries />} />
                  <Route path="/hr/recruitment" element={<Recruitment />} />
                  <Route path="/hr/leave" element={<LeaveManagement />} />
                  <Route path="/hr/payroll" element={<Payroll />} />
                  <Route path="/hr/payroll/settings" element={<PayrollSettings />} />
                  <Route path="/hr/payroll/run/:runId" element={<PayrollRunDetail />} />
                  
                  {/* Finance Routes */}
                  <Route path="/finance/accounts" element={<ChartOfAccounts />} />
                  <Route path="/finance/journals" element={<JournalEntries />} />
                  <Route path="/finance/general-ledger" element={<GeneralLedger />} />
                  <Route path="/finance/trial-balance" element={<TrialBalance />} />
                  <Route path="/finance/statements" element={<FinancialStatements />} />
                  <Route path="/finance/budgets" element={<BudgetPlanning />} />
                  <Route path="/finance/budget-performance" element={<BudgetPerformance />} />
                  <Route path="/consolidation" element={<Consolidation />} />
                  <Route path="/reports" element={<Reports />} />
                  <Route path="/settings" element={<Settings />} />
                  <Route path="/settings/users" element={<UserManagement />} />
                  
                  {/* DFS Financial Services Routes */}
                  <Route path="/dfs/dashboard" element={<DFSDashboard />} />
                  <Route path="/dfs/teller-accounts" element={<TellerAccounts />} />
                  <Route path="/dfs/allocations" element={<VaultAllocations />} />
                  <Route path="/dfs/teller" element={<TellerDashboard />} />
                  <Route path="/dfs/teller-dashboard" element={<TellerDashboard />} />
                  <Route path="/dfs/cash-position" element={<CashPosition />} />
                  <Route path="/dfs/stock" element={<StockManagement />} />
                  <Route path="/dfs/customers" element={<Customers />} />
                  <Route path="/dfs/loans" element={<Loans />} />
                </Routes>
              </Suspense>
            </Layout>
          </PrivateRoute>
        }
      />
    </Routes>
  );
}

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <AuthProvider>
          <CompanyProvider>
            <AppRoutes />
            <Toaster position="top-right" richColors />
          </CompanyProvider>
        </AuthProvider>
      </BrowserRouter>
    </QueryClientProvider>
  );
}

export default App;
